/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { KEYCODE } from '../../utils/keycode.js';
/**
 * @prop {String} label - Defines the label of the dropdown. Will be shown as a placeholder if the value is empty.
 * @prop {String} value - The current value. Reflects the label of the selected item.
 * @prop {String} icon - Optional property that reflects the icon of the selected item.
 * @prop {Boolean} disabled - If set to `true`, input won't be allowed and disabled styles will be applied.
 * @prop {Boolean} mandatory - If set to `true`, the dropdown displays styles for mandatory input.
 * @prop {Boolean} readonly - If set to `true`, the field will be set to readonly and thus won't allow user input.
 * @prop {Boolean} error - If set to `true`, the dropdown displays styles for invalid input.
 * @prop {Boolean} safety - If set to `true`, the dropdown displays styles for safety-relevant input (yellow outline).
 * @prop {String} errorMessage - Displays a message in a tooltip when error property is set to true.
 * @prop {String} hint - Displays a message below the input box.
 * @prop {Boolean} active - *hide Indicates whether dropdown is active.
 * @prop {Boolean} success - If set to `true`, the dropdown will render in success style.
 * @prop {Boolean} condensed - If dropdown is inside a table, then the condensed format of dropdown is used to display information. This feature cannot be overriden.
 * @prop {Boolean} multiSelect - If set to `true`, the dropdown displays items with checkbox which allows multiple items selection.
 * @prop {Boolean} noBorder - If set to `true`, the bottom border will not be displayed.
 * @prop {Boolean} editable - If set to `true`, the dropdown input becomes editable and filters / selects menu-items based on entered value.
 * @prop {Number} items - Defines the max-height of the menu based on the property value. A scroll bar appears if the number of menu-items exceed the property value.
 * @slot - unnamed: Holds the array of items that will be shown as options. Takes Menu Items as children.
 */
export class SHDropdown extends LitElement {
  static get properties() {
    return {
      label: { type: String, reflect: true },
      value: { type: String, reflect: true },
      hint: { type: String, reflect: true },
      icon: { type: String, reflect: true },
      active: { type: Boolean, reflect: true },
      /** @ignore */
      errorMessage: { type: String, reflect: true, attribute: 'error-message' },
      readonly: { type: Boolean, reflect: true },
      disabled: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      /** @ignore */
      _currentFocus: { type: Number },
      /** @ignore */
      multiSelect: { type: Boolean, reflect: true, attribute: 'multi-select' },
      /** @ignore */
      noBorder: { type: Boolean, reflect: true, attribute: 'no-border' },
      /** @ignore */
      noClear: { type: Boolean, reflect: true, attribute: 'no-clear' },
      mandatory: { type: Boolean, reflect: true },
      error: { type: Boolean, reflect: true },
      success: { type: Boolean, reflect: true },
      safety: { type: Boolean, reflect: true },
      editable: { type: Boolean, reflect: true },
      items: { type: Number, reflect: true },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          width: 100%;
          height: auto;
          position: relative;
          display: block;
        }
        .dropdown-label {
          color: var(--text-secondary);
          position: absolute;
          left: 0.5rem;
          width: calc(100% - 2.5rem);
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          top: 0.5rem;
          font: var(--body-1);
          line-height: 1.5rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          transition: var(--transition-time) all ease-in-out;
          display: flex;
        }
        :host(:not(.empty)) .dropdown-label,
        :host([active]) .dropdown-label {
          top: 0.25rem;
          right: 0.25rem;
          font: var(--body-2);
          line-height: 1rem;
        }
        :host(:hover:not([no-clear]):not(.empty)) .dropdown-label {
          width: calc(100% - 4.5rem);
        }
        :host(:hover[icon]:not([no-clear])) .dropdown-label {
          width: calc(100% - 7.5rem);
        }
        :host(:hover[error]:not([no-clear]):not(.empty)) .dropdown-label,
        :host(:hover[success]:not([no-clear]):not(.empty)) .dropdown-label {
          width: calc(100% - 6.5rem);
        }
        :host(:hover[icon][error]:not([no-clear])) .dropdown-label,
        :host(:hover[icon][success]:not([no-clear])) .dropdown-label {
          width: calc(100% - 9.5rem);
        }
        .label {
          overflow: hidden;
          white-space: nowrap;
          text-overflow: ellipsis;
          flex: 1;
          max-width: fit-content;
          max-width: -moz-fit-content;
          transition: none;
        }
        sh-text[size='body-1'] {
          line-height: 1.5rem;
        }
        input {
          outline: none;
          border: none;
          cursor: pointer;
          font: var(--body-1);
          line-height: 1 rem;
          color: var(--text-primary);
          background: rgba(var(--ui-1), var(--opacity-7));
          height: 2.5rem;
          border-radius: 0.125rem 0.125rem 0 0;
          width: 100%;
          box-sizing: border-box;
          padding: 1.25rem 2.5rem 0.25rem 0.5rem;
          -webkit-font-smoothing: antialiased;
          text-rendering: optimizeLegibility;
          -moz-osx-font-smoothing: grayscale;
          text-overflow: ellipsis;
          border-bottom: 0.0625rem solid transparent;
          border-color: rgba(var(--ui-1), var(--opacity-4));
        }
        :host([active]) input {
          outline: none;
          border-color: rgba(var(--ui-1), var(--opacity-2));
        }
        :host(:hover:not([no-clear])) input {
          padding-right: 4.5rem;
        }
        :host(:hover[error]:not([readonly]):not([no-clear])) input,
        :host(:hover[success]:not([readonly]):not([no-clear])) input {
          padding-right: 6.5rem;
        }
        .dropdown-wrapper {
          display: flex;
          flex-direction: row;
          position: relative;
        }
        .content-wrapper {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        #dropdown-icon {
          position: absolute;
          top: 0.5rem;
          right: 0.25rem;
          transition: var(--transition-time) color ease-in-out, 0s transform;
          margin: -0.25rem 0;
        }

        :host([active]) #dropdown-icon {
          transform: rotate(180deg);
        }

        .overlay {
          min-width: 0;
          overflow: auto;
          padding: 0 0.5rem;
          background: var(--base-4);
          box-shadow: var(--shadow-overlay);
          box-sizing: border-box;
          border-radius: 0.125rem;
          display: none;
        }
        :host(:not([items])) .overlay {
          max-height: 13.75rem !important;
        }
        :host([active]) .overlay {
          display: block;
          max-width: unset !important;
        }
        :host([disabled]) {
          pointer-events: none;
          cursor: not-allowed;
        }
        :host([disabled]) .dropdown-label,
        :host([disabled]) input {
          color: var(--text-disabled);
          opacity: unset;
        }
        /* Validation Styles */
        #mandatory {
          display: none;
          color: rgba(var(--ui-2), var(--opacity-1));
        }
        :host([disabled]) #mandatory {
          color: rgba(var(--ui-2), var(--opacity-5));
        }
        :host([mandatory]) #mandatory {
          display: inline-block;
        }
        :host([error]) input,
        :host([status='error']) input {
          border-color: rgba(var(--functional-red), var(--opacity-1));
        }
        :host([success]) input,
        :host([status='success']) input {
          border-color: rgba(var(--functional-green), var(--opacity-1));
        }
        :host([safety]) input,
        :host([status='safety']) input {
          border-color: rgba(var(--functional-yellow), var(--opacity-2));
        }
        :host([readonly]) input {
          background: transparent;
          cursor: default;
        }
        :host([readonly]) #dropdown-icon {
          display: none;
        }
        :host([readonly]) input:focus + .dropdown-label {
          color: var(--text-secondary);
        }
        :host([readonly]),
        :host([readonly]) * {
          pointer-events: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }

        /* Clear */
        #clear {
          opacity: 0;
          pointer-events: none;
          position: absolute;
          transition: var(--transition-time) all ease-in-out;
          cursor: pointer;
          right: 2.5rem;
          top: 0.5rem;
        }
        :host(:hover) #clear,
        :host(:focus) #clear,
        :host(:hover[error]) #clear,
        :host(:hover[success]) #clear,
        #clear:focus {
          opacity: 1;
          pointer-events: all;
        }
        :host([error]:not(.empty)) #clear,
        :host([success]:not(.empty)) #clear {
          right: 4.5rem;
        }
        /* condensed styles */
        :host([condensed]) {
          height: auto;
        }
        :host([condensed]:not(.empty)) .dropdown-label {
          display: none;
        }
        :host([condensed]) .dropdown-label,
        :host([condensed]) #clear {
          top: 0.25rem;
        }
        :host([condensed]) input,
        :host([condensed]) input:focus {
          padding: 0.25rem 2.5rem 0.25rem 0.5rem;
          height: 2rem;
        }
        :host([condensed]) #dropdown-icon {
          margin-top: -0.5rem;
        }
        /* icon */
        :host([icon]) input {
          padding-left: 3rem;
        }
        :host([condensed][icon][active][no-clear]) input,
        :host([condensed][icon][no-clear]) input,
        :host(:not([no-clear])[condensed][icon]) input {
          padding-left: 3rem !important;
        }
        :host(:hover:not([no-clear])[condensed][icon]:not([error]):not([success])) input,
        :host(:hover:not([no-clear])[condensed][active]:not([error]):not([success])) input {
          padding-right: 4.5rem;
        }
        :host([icon]) .dropdown-label {
          padding-left: 2.5rem;
          width: calc(100% - 6rem);
        }
        :host([icon][error]) .dropdown-label,
        :host([icon][success]) .dropdown-label {
          width: calc(100% - 7.5rem);
        }
        :host([editable][condensed][active]) .dropdown-label {
          display: none;
        }
        .dropdown-icon {
          position: absolute;
          top: 0.25rem;
          left: 0.5rem;
        }
        :host([condensed]) .dropdown-icon {
          min-height: 1.5rem;
          min-width: 1.5rem;
          font-size: 1.5rem;
          line-height: 1.625rem;
        }
        :host([condensed]) .status-icon {
          top: 0.25rem;
        }
        /* hover */
        :host(:hover:not([active]):not(.no-hovermq)) input,
        :host(.hover:not([active])) input {
          background: rgba(var(--ui-1), var(--opacity-6));
        }
        /* helper text */
        .helper-text {
          padding: 0.25rem 0.5rem 0rem 0.5rem;
          box-sizing: border-box;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        :host([disabled]) .helper-text {
          color: var(--text-disabled);
        }
        /* status icons */
        .status-icon {
          position: absolute;
          top: 0.5rem;
          right: 2.5rem;
        }
        :host([readonly]) .status-icon {
          right: 0.5rem;
        }
        :host([success]) .dropdown-label,
        :host([error]) .dropdown-label {
          width: calc(100% - 3rem - 2rem);
        }
        :host([success]:not([readonly])) input,
        :host([error]:not([readonly])) input {
          padding-right: 4.5rem;
        }
        :host([no-border]) input {
          border-bottom: none;
        }
        ::slotted([class='hidden']) {
          display: none;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="dropdown-wrapper">
        <div class="content-wrapper">
          ${this.icon && !this.multiSelect
            ? html` <sh-icon
                icon="${this.icon}"
                tabindex="-1"
                class="dropdown-icon"
                ?disabled="${this.disabled}"
              ></sh-icon>`
            : ''}
          <input
            id="dropdownInput"
            ?readonly="${!this.editable}"
            @input="${this._inputChangeListener}"
            ​​​​
            type="text"
            .value="${this.value ? this.value : ''}"
            ?disabled="${this.disabled}"
          />
          <div class="dropdown-label">
            ${this.label && !(this.condensed && this.value)
              ? html` <sh-text
                  .size="${!this.value && !this.active ? 'body-1' : 'body-2'}"
                  .color=${this.disabled ? 'disabled' : 'secondary'}
                  class="label"
                  >${this.label}
                </sh-text>`
              : ''}
            ${this.mandatory ? html`<span id="mandatory"> *</span>` : ''}
          </div>
        </div>
        ${this.value && !this.noClear && !this.disabled && !this.readonly
          ? html`
            <sh-icon button size='s' icon='cancel' tabindex='0' @click=${this._resetValue}' id='clear'></sh-icon>`
          : ''}
        ${this.error
          ? html` <sh-icon
              icon="error"
              tabindex="-1"
              class="status-icon"
              size="s"
              ?disabled="${this.disabled}"
            ></sh-icon>`
          : ''}
        ${this.success
          ? html` <sh-icon
              icon="success"
              tabindex="-1"
              class="status-icon"
              size="s"
              ?disabled="${this.disabled}"
            ></sh-icon>`
          : ''}
        <sh-icon
          button
          icon="down-s"
          id="dropdown-icon"
          tabindex="0"
          ?disabled="${this.disabled || this.readonly}"
        ></sh-icon>
        <sh-overlay
          class="overlay"
          style="width:${getComputedStyle(this).width};"
          @click="${(event) => (this.multiSelect ? event.stopPropagation() : '')}"
          scroll-action="cancel"
          no-overlap
          horizontal-align="left"
        >
          <slot id="dropdownMenu" @slotchange="${this._handleSlotChange}"></slot>
        </sh-overlay>
      </div>
      ${this.error && this.errorMessage
        ? html` <sh-text class="helper-text error-message" size="body-2" color="secondary"
            >${this.errorMessage}</sh-text
          >`
        : ''}
      ${this.hint && !this.errorMessage
        ? html`
            <sh-text size="body-2" class="helper-text hint" color="secondary">${this.hint}</sh-text>
          `
        : ''}
      ${this.hint && this.errorMessage && !this.error
        ? html` <sh-text size="body-2" class="helper-text hint" color="secondary"
            >${this.hint}</sh-text
          >`
        : ''}
    `;
  }

  constructor() {
    super();
    this.value = '';
    this.label = 'label';
    this._currentFocus = -1;
    this.noClear = true;
    this._multiValue = [];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'listbox');
    this._handleMenuItemActiveChange = this._updateDropdown.bind(this);
    this.addEventListener('active-changed', this._handleMenuItemActiveChange);
    this.addEventListener('click', this._handleClick);
    this.resizeListener = this._handleResize.bind(this);
    window.addEventListener('resize', this.resizeListener);
  }

  updated(changedProperties) {
    if (changedProperties.has('active')) {
      this._toggleDropdown();
    }
    if (changedProperties.has('value')) {
      this._valueObserver();
    }
    if (changedProperties.has('multiSelect')) {
      this._addMenuItemCheckboxes();
    }
    if (changedProperties.has('items')) {
      this._updateMaxHeight();
    }
    const listOfProperties = [
      'label',
      'value',
      'hint',
      'active',
      'icon',
      'errorMessage',
      'readonly',
      'disabled',
      'condensed',
      'multiSelect',
      'noBorder',
      'noClear',
      'mandatory',
      'error',
      'success',
      'safety',
      'editable',
    ];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }

  _updateMaxHeight() {
    const overlay = this.shadowRoot.querySelector('.overlay');
    if (this.items) {
      overlay.sizingTarget.style.maxHeight = `${this.items * 2.5}rem`;
      if (overlay._fitInfo) {
        overlay._fitInfo.sizerInlineStyle.maxHeight = `${this.items * 2.5}rem`;
      }
    }
  }

  _inputChangeListener(e) {
    const menuItems = this.querySelectorAll('sh-menu-item');
    let value = (this.value = e.target.value);
    let itemData;

    this._hideCategories();
    for (let menuItem of menuItems) {
      itemData = menuItem.value || menuItem.label;
      if (itemData.toLowerCase().indexOf(value.toLowerCase()) === -1) {
        menuItem.classList.add('hidden');
      } else {
        menuItem.classList.remove('hidden');
      }
    }
    if (this.editable) {
      this.shadowRoot.querySelector('sh-overlay')._fireResize();
    }
  }

  _hideCategories() {
    const categories = this.querySelectorAll(':not(sh-menu-item)');
    if (categories.length > 0) {
      for (let category of categories) {
        category.classList.add('hidden');
      }
    }
  }

  firstUpdated() {
    const label = this.shadowRoot.querySelector('.label');
    const overlay = this.shadowRoot.querySelector('.overlay');

    overlay.addEventListener('opened', () => {
      if (this.items) {
        overlay.sizingTarget.style.maxHeight = `${2.5 * this.items}rem`;
      }
    });
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('no-hovermq');
    }
    this.addEventListener('overlay-canceled', this._overlayCancelledCallback);
    this.addEventListener('focus', () => this._handleFocus());
    //To prevent scrolling of parent element
    this.addEventListener('keydown', (e) => this._handleKeydown(e));

    this.shadowRoot.querySelector('#dropdownInput').addEventListener('mouseover', function () {
      if (this.offsetWidth < this.scrollWidth) {
        this.setAttribute('title', this.value);
      } else {
        this.removeAttribute('title');
      }
    });
    if (label) {
      label.addEventListener('mouseover', () => {
        if (label.offsetWidth < label.scrollWidth) {
          label.setAttribute('title', this.label);
        } else {
          label.removeAttribute('title');
        }
      });
    }
  }

  _handleResize() {
    this.active = false;
  }

  disconnectedCallback() {
    this.removeEventListener('active-changed', this._handleMenuItemActiveChange);
    window.removeEventListener('resize', this.resizeListener);
    super.disconnectedCallback();
  }

  _addMenuItemCheckboxes() {
    const menuItems = this.querySelectorAll('sh-menu-item');
    for (let menuItem of menuItems) {
      menuItem.checkbox = true;
    }
  }
  _handleSlotChange() {
    if (this.multiSelect) {
      this._addMenuItemCheckboxes();
    }
  }
  _valueObserver() {
    if (this.value) {
      this.classList.remove('empty');
      this._updateMenuItemState();
    } else {
      this.classList.add('empty');
      this.icon = null;
      if (this.editable) {
        this._removeHiddenClass();
      }
    }
  }

  // Sets active property of required menuItem when dropdown value is set
  _updateMenuItemState() {
    if (this.multiSelect) {
      return;
    }
    let menuItemFound = false;
    const menuItems = this.querySelectorAll('sh-menu-item');
    let activeMenuItemCount = 0;
    for (let menuItem of menuItems) {
      const itemData = menuItem.value || menuItem.label;
      const itemIcon = menuItem.icon;
      if (this.icon && this.value === itemData && this.icon === itemIcon) {
        menuItem.active = true;
        menuItemFound = true;
        activeMenuItemCount += 1;
        this._removeHiddenClass();
      } else if (!this.icon && this.value === itemData) {
        menuItem.active = true;
        menuItemFound = true;
        activeMenuItemCount += 1;
        this._removeHiddenClass();
      } else {
        menuItem.active = false;
      }
    }
    if (activeMenuItemCount > 1) {
      console.warn(
        `Multiple menu-items are found to be active. This happens when no difference is found in terms of label or icon used.
        If not multi-select it is advisable to have only a single menu-item as active.`
      );
    }
    if (!menuItemFound) {
      this.icon = null;
    }
  }

  _updateDropdown(e) {
    if (e.target === this) {
      return;
    }
    const menuItem = e.target;
    if (this.multiSelect) {
      this._updateMultiSelectDropdownValue(menuItem);
    } else {
      this._updateDropdownValue(menuItem);
    }
  }

  _overlayCancelledCallback(e) {
    const event = e.detail;
    if (event.type === 'click' && event.target !== this) {
      this.active = false;
    } else if (event.type === 'keydown' && event.keyCode === KEYCODE.ESCAPE) {
      this.active = false;
    } else if (event.type === 'scroll') {
      this.active = false;
    }
  }

  _handleClick(e) {
    const input = this.shadowRoot.querySelector('#dropdownInput');
    if (e.target === this) {
      this.active = !this.active;
    }
    this.active ? input.focus({ preventScroll: true }) : input.blur();
  }

  _handleKeydown(e) {
    if (e.keyCode === KEYCODE.ARROW_UP || e.keyCode === KEYCODE.ARROW_DOWN) {
      e.preventDefault();
      e.stopPropagation();
    } else if (e.keyCode === KEYCODE.TAB) {
      this.active = false;
      this._toggleDropdown();
    }
  }

  _toggleDropdown() {
    const overlay = this.shadowRoot.querySelector('.overlay');
    if (this.active) {
      overlay.open();
    } else {
      overlay.close();
      this._resetMenuItemHoverStyle();
      this._currentFocus = -1;
    }
  }

  _updateDropdownValue(menuItem) {
    const itemData = menuItem.value || menuItem.label;
    if (menuItem.active) {
      this.value = itemData;
      this._updateDropdownIcon(menuItem);
      this._unselectSiblings(menuItem);
      this.active = false;
    } else if (!menuItem.active && this.value === itemData) {
      if (!this.icon) {
        menuItem.active = true;
      } else {
        menuItem.active = this.icon === menuItem.icon;
      }
    }
  }

  _updateDropdownIcon(menuItem) {
    if (this.value && menuItem.active && !this.multiSelect) {
      this.icon = menuItem.icon;
    } else if (!this.value || this.multiSelect) {
      this.removeAttribute('icon');
    }
  }

  _unselectSiblings(menuItem) {
    const activeItems = this.querySelectorAll('sh-menu-item[active]');
    for (let i = 0; i < activeItems.length; i++) {
      if (this.icon && this.icon !== activeItems[i].icon) {
        activeItems[i].active = false;
      } else if (
        (menuItem.label && activeItems[i].label !== menuItem.label) ||
        (menuItem.value && activeItems[i].value !== menuItem.value)
      ) {
        activeItems[i].active = false;
      }
    }
  }

  _updateMultiSelectDropdownValue(menuItem) {
    const itemData = menuItem.value || menuItem.label;
    if (menuItem.active) {
      this._multiValue.push(itemData);
    } else {
      this._multiValue = this._multiValue.filter((val) => val !== itemData);
    }
    this.value = this._multiValue.toString();
  }

  _resetValue(e) {
    e.stopPropagation();
    if (this.editable) {
      this._removeHiddenClass();
    }
    this.active = false;
    this.value = '';
    this._unselectActiveItems();
  }

  _removeHiddenClass() {
    const children = this.querySelectorAll('*');
    for (let child of children) {
      child.classList.remove('hidden');
    }
  }

  _resetMenuItemHoverStyle() {
    if (this._currentFocus !== -1) {
      const menuItems = this.querySelectorAll('sh-menu-item.hover');
      for (let menuItem of menuItems) {
        menuItem.classList.remove('hover');
        menuItem.classList.remove('focus');
        menuItem.blur();
      }
    }
  }

  _unselectActiveItems() {
    const activeItems = this.querySelectorAll('sh-menu-item[active]');
    for (let i = 0; i < activeItems.length; i++) {
      activeItems[i].active = false;
    }
  }

  _handleFocus() {
    this.onkeyup = (e) => {
      this._keyEvents(e);
    };
  }

  _keyEvents(e) {
    if (e.keyCode === KEYCODE.ENTER) {
      const source = this._getEventSource(e);
      if (source.id === 'dropdownInput' && !this.readonly) {
        this.active = !this.active;
      }
    } else if (e.keyCode === KEYCODE.ARROW_DOWN) {
      const menuItems = this.querySelectorAll(`sh-menu-item:not([class='hidden'])`);

      if (menuItems.length !== 0) {
        this._currentFocus = ++this._currentFocus % menuItems.length;
        this._updateFocusedItem(e);
      }
    } else if (e.keyCode === KEYCODE.ARROW_UP) {
      const menuItems = this.querySelectorAll(`sh-menu-item:not([class='hidden'])`);

      if (menuItems.length !== 0) {
        this._decrementCurrentFocus(menuItems);
        this._updateFocusedItem(e);
      }
    }
  }

  _updateFocusedItem(e) {
    e.stopImmediatePropagation();
    e.preventDefault();
    const menuItems = this.querySelectorAll(`sh-menu-item:not([class='hidden'])`);
    this._resetMenuItemHoverStyle();
    menuItems[this._currentFocus].classList.add('hover');
    menuItems[this._currentFocus].classList.add('focus');
    menuItems[this._currentFocus].focus();
  }

  _decrementCurrentFocus(menuItems) {
    if (this._currentFocus === -1) {
      this._currentFocus = (this._currentFocus + menuItems.length) % menuItems.length;
    } else {
      this._currentFocus = (this._currentFocus - 1 + menuItems.length) % menuItems.length;
    }
  }

  _getEventSource(e) {
    const source = e.composedPath() ? e.composedPath()[0] : null;
    return source;
  }
}

customElements.define('sh-dropdown', SHDropdown);
